/*
 * HussarAi MOS PreCheck — Polish-first applicant wizard.
 *
 * Doctrine — surfaced verbatim: "AI prepares. Humans decide."
 *
 * Four steps: consent → upload → review → report. All copy is neutral
 * ("review signal", "needs human review", "potential inconsistency").
 * No decision vocabulary anywhere in this file.
 *
 * The wizard talks only to the backend routes registered by
 * services/backend/routes/mos_precheck_routes.py.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mos-precheck`;
const ACTIVATION_API = `${BACKEND_URL}/api/analytics/track-activation`;

// Fire an activation funnel event. Never throws, never blocks the UI.
const trackActivation = (event_type) => {
  try {
    axios
      .post(ACTIVATION_API, { event_type }, { withCredentials: true })
      .catch(() => {});
  } catch (_e) {
    /* telemetry must never break user flow */
  }
};

const STEPS = ['consent', 'upload', 'review', 'report'];

const shellStyles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0B1220 0%, #0F172A 100%)',
    color: '#E2E8F0',
    padding: '64px 24px 96px',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  },
  container: { maxWidth: 960, margin: '0 auto' },
  backLink: {
    display: 'inline-block',
    color: '#94A3B8',
    marginBottom: 24,
    textDecoration: 'none',
    fontSize: 14,
  },
  eyebrow: {
    fontSize: 12,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: '#94A3B8',
    marginBottom: 12,
  },
  h1: {
    fontSize: 32,
    lineHeight: 1.2,
    margin: '0 0 16px',
    fontWeight: 600,
    color: '#F8FAFC',
  },
  subtitle: { fontSize: 16, color: '#CBD5F5', marginBottom: 32 },
  card: {
    background: 'rgba(15, 23, 42, 0.7)',
    border: '1px solid #1E293B',
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    backdropFilter: 'blur(12px)',
  },
  stepBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    fontSize: 12,
    color: '#94A3B8',
    marginBottom: 24,
  },
  stepBadge: (active) => ({
    padding: '6px 12px',
    borderRadius: 999,
    background: active ? '#1E293B' : 'transparent',
    border: '1px solid #334155',
    color: active ? '#F1F5F9' : '#94A3B8',
  }),
  primaryBtn: {
    background: '#F59E0B',
    color: '#0B1220',
    border: 'none',
    padding: '12px 20px',
    borderRadius: 10,
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryBtn: {
    background: 'transparent',
    color: '#F1F5F9',
    border: '1px solid #334155',
    padding: '10px 16px',
    borderRadius: 10,
    cursor: 'pointer',
  },
  disabledBtn: {
    opacity: 0.5, cursor: 'not-allowed',
  },
  safetyStrip: {
    background: 'rgba(148, 163, 184, 0.08)',
    borderLeft: '3px solid #94A3B8',
    padding: '12px 16px',
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 14,
    color: '#CBD5F5',
  },
  factCard: {
    background: 'rgba(148, 163, 184, 0.06)',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    fontSize: 13,
    color: '#CBD5F5',
  },
  errorBox: {
    background: 'rgba(239, 68, 68, 0.08)',
    borderLeft: '3px solid #F87171',
    padding: '12px 16px',
    borderRadius: 6,
    color: '#FCA5A5',
    marginBottom: 16,
    fontSize: 14,
  },
  chip: (tone) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: 11,
    marginRight: 8,
    background:
      tone === 'high' ? 'rgba(239,68,68,0.15)' :
      tone === 'medium' ? 'rgba(245,158,11,0.15)' :
      'rgba(148,163,184,0.15)',
    color:
      tone === 'high' ? '#FCA5A5' :
      tone === 'medium' ? '#FCD34D' :
      '#CBD5F5',
  }),
};

const StepBar = ({ current }) => {
  const { t } = useTranslation();
  return (
    <div style={shellStyles.stepBar} data-testid="mos-wizard-step-bar">
      {STEPS.map((key) => (
        <span key={key} style={shellStyles.stepBadge(current === key)} data-testid={`mos-step-${key}`}>
          {t(`mos.wizard.steps.${key}`)}
        </span>
      ))}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Step 1 — consent
// ----------------------------------------------------------------------------

const ConsentStep = ({ onCreated, variants }) => {
  const { t } = useTranslation();
  const [variant, setVariant] = useState('temporary_residence_work');
  const [consent, setConsent] = useState(false);
  const [safety, setSafety] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const activeVariants = variants?.active_variants || ['temporary_residence_work'];

  const handleContinue = useCallback(async () => {
    setError('');
    if (!consent || !safety) {
      setError(t('mos.consent.acknowledgeRequired'));
      return;
    }
    // Activation funnel: fire before the network call so the click is
    // captured even if case creation fails validation.
    trackActivation('activation_mos_precheck_start_clicked');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/cases`, {
        procedure_variant: variant,
        consent_acknowledged: true,
        safety_acknowledged: true,
        preferred_language: 'pl',
      }, { withCredentials: true });
      onCreated(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail?.message || t('mos.upload.uploadError'));
    } finally {
      setSubmitting(false);
    }
  }, [consent, safety, variant, onCreated, t]);

  return (
    <div style={shellStyles.card} data-testid="mos-consent-step">
      <h2 style={{ ...shellStyles.h1, fontSize: 22 }}>{t('mos.consent.title')}</h2>
      <p style={shellStyles.safetyStrip} data-testid="mos-consent-safety">
        {t('mos.consent.safety')}
      </p>

      <label style={{ display: 'block', marginBottom: 16, fontSize: 14 }}>
        {t('mos.consent.variantLabel')}:
        <select
          value={variant}
          onChange={(e) => setVariant(e.target.value)}
          data-testid="mos-consent-variant"
          style={{
            marginLeft: 12,
            background: '#0F172A',
            color: '#F1F5F9',
            border: '1px solid #334155',
            padding: '6px 10px',
            borderRadius: 8,
          }}
        >
          {activeVariants.map((v) => (
            <option key={v} value={v}>
              {t(`mos.variantLabels.${v}`)}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: 'flex', gap: 8, marginBottom: 12, fontSize: 14 }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="mos-consent-checkbox-independence"
        />
        <span>{t('mos.consent.consentBox')}</span>
      </label>
      <label style={{ display: 'flex', gap: 8, marginBottom: 16, fontSize: 14 }}>
        <input
          type="checkbox"
          checked={safety}
          onChange={(e) => setSafety(e.target.checked)}
          data-testid="mos-consent-checkbox-safety"
        />
        <span>{t('mos.consent.safetyBox')}</span>
      </label>

      {error && <div style={shellStyles.errorBox} data-testid="mos-consent-error">{error}</div>}

      <button
        style={{ ...shellStyles.primaryBtn, ...(submitting ? shellStyles.disabledBtn : {}) }}
        disabled={submitting}
        onClick={handleContinue}
        data-testid="mos-consent-continue"
      >
        {submitting ? '…' : t('mos.consent.continueCta')}
      </button>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Step 2 — upload
// ----------------------------------------------------------------------------

const UploadStep = ({ caseInfo, onDone }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [hintDocType, setHintDocType] = useState('');
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cases/${caseInfo.case_id}/documents`, { withCredentials: true });
      setDocuments(res.data.documents || []);
    } catch {
      /* keep prior state */
    }
  }, [caseInfo.case_id]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (hintDocType) form.append('hint_document_type', hintDocType);
      await axios.post(`${API}/cases/${caseInfo.case_id}/documents`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail?.reason || t('mos.upload.uploadError'));
    } finally {
      setUploading(false);
    }
  }, [caseInfo.case_id, hintDocType, refresh, t]);

  return (
    <div style={shellStyles.card} data-testid="mos-upload-step">
      <h2 style={{ ...shellStyles.h1, fontSize: 22 }}>{t('mos.upload.title')}</h2>
      <div style={shellStyles.safetyStrip}>{t('mos.upload.hint')}</div>

      <label style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
        {t('mos.upload.docTypeLabel')}:
        <input
          type="text"
          value={hintDocType}
          onChange={(e) => setHintDocType(e.target.value)}
          data-testid="mos-upload-hint-input"
          style={{
            marginLeft: 12,
            background: '#0F172A',
            color: '#F1F5F9',
            border: '1px solid #334155',
            padding: '6px 10px',
            borderRadius: 8,
            width: 260,
          }}
        />
      </label>

      <label style={{ ...shellStyles.primaryBtn, display: 'inline-block', marginBottom: 16 }} data-testid="mos-upload-picker">
        {uploading ? t('mos.upload.uploadingCta') : t('mos.upload.dropCta')}
        <input
          type="file"
          accept="application/pdf"
          onChange={handleUpload}
          data-testid="mos-upload-input"
          style={{ display: 'none' }}
        />
      </label>

      {error && <div style={shellStyles.errorBox} data-testid="mos-upload-error">{error}</div>}

      <h3 style={{ fontSize: 16, marginTop: 24 }}>{t('mos.upload.listHeading')}</h3>
      {documents.length === 0 ? (
        <p style={{ color: '#94A3B8', fontSize: 14 }} data-testid="mos-upload-empty">
          {t('mos.upload.empty')}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }} data-testid="mos-upload-list">
          {documents.map((d) => (
            <li key={d.document_id} style={shellStyles.factCard}>
              <span style={shellStyles.chip(d.extraction_status === 'extracted' ? 'low' : d.extraction_status === 'needs_human' ? 'medium' : 'high')}>
                {t(`mos.upload.extractionStatus.${d.extraction_status || 'pending'}`)}
              </span>
              <strong>{d.safe_filename}</strong> · {(d.size_bytes / 1024).toFixed(1)} KB · {d.extracted_fact_count} fact(s)
              {d.extraction_message_pl && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#94A3B8' }}>{d.extraction_message_pl}</div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div style={{ marginTop: 24 }}>
        <button
          style={{ ...shellStyles.primaryBtn, ...(documents.length === 0 ? shellStyles.disabledBtn : {}) }}
          disabled={documents.length === 0}
          onClick={onDone}
          data-testid="mos-upload-continue"
        >
          {t('mos.upload.continueCta')}
        </button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Step 3 — review facts
// ----------------------------------------------------------------------------

const ReviewStep = ({ caseInfo, onReport }) => {
  const { t } = useTranslation();
  const [facts, setFacts] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/cases/${caseInfo.case_id}/facts`, { withCredentials: true });
        setFacts(res.data.facts || []);
      } catch {
        /* keep empty */
      }
    })();
  }, [caseInfo.case_id]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/cases/${caseInfo.case_id}/generate-report`, {}, { withCredentials: true });
      onReport(res.data);
    } finally {
      setGenerating(false);
    }
  }, [caseInfo.case_id, onReport]);

  return (
    <div style={shellStyles.card} data-testid="mos-review-step">
      <h2 style={{ ...shellStyles.h1, fontSize: 22 }}>{t('mos.review.title')}</h2>
      {facts.length === 0 ? (
        <p style={{ color: '#94A3B8' }} data-testid="mos-review-empty">{t('mos.review.empty')}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }} data-testid="mos-review-list">
          {facts.map((f) => (
            <li key={f.fact_id} style={shellStyles.factCard}>
              <strong>{t(`mos.review.factTypes.${f.fact_type}`, f.fact_type)}</strong>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                {f.value_iso || f.value_masked || f.value_code || f.value_text || ''}
                {f.snippet ? ` · "${f.snippet}"` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
      <button
        style={{ ...shellStyles.primaryBtn, marginTop: 24, ...(generating ? shellStyles.disabledBtn : {}) }}
        disabled={generating}
        onClick={handleGenerate}
        data-testid="mos-review-generate"
      >
        {generating ? t('mos.review.generating') : t('mos.review.generateCta')}
      </button>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Step 4 — report
// ----------------------------------------------------------------------------

const ReportStep = ({ caseInfo, reportData, onDeleted }) => {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);

  const report = reportData?.report || {};
  const integ = report.record_integrity || {};
  const proc = report.procedure_requirements || {};
  const overallRisk = report.overall_risk_level || 'low';

  const handleDownload = useCallback(async () => {
    const res = await axios.get(`${API}/cases/${caseInfo.case_id}/report.md?language=pl`, {
      withCredentials: true,
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mos-precheck-${caseInfo.case_id}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [caseInfo.case_id]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(t('mos.report.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/cases/${caseInfo.case_id}`, { withCredentials: true });
      onDeleted();
    } finally {
      setDeleting(false);
    }
  }, [caseInfo.case_id, onDeleted, t]);

  return (
    <div style={shellStyles.card} data-testid="mos-report-step">
      <h2 style={{ ...shellStyles.h1, fontSize: 22 }}>{t('mos.report.title')}</h2>
      <div style={shellStyles.safetyStrip} data-testid="mos-report-independence">
        {t('mos.independenceStatement')}
      </div>
      <p style={{ fontSize: 14, color: '#CBD5F5', marginBottom: 24 }} data-testid="mos-report-disclaimer">
        {t('mos.disclaimer')}
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <span style={shellStyles.chip(overallRisk)} data-testid="mos-report-risk">
          {t('mos.report.overallRisk')}: {t(`mos.report.risk${overallRisk.charAt(0).toUpperCase()}${overallRisk.slice(1)}`)}
        </span>
      </div>

      <h3 style={{ fontSize: 16 }}>{t('mos.report.recordIntegrity')}</h3>
      {(integ.findings || []).length === 0 ? (
        <p style={{ color: '#94A3B8', fontSize: 14 }}>—</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }} data-testid="mos-report-integrity-list">
          {integ.findings.map((f, i) => (
            <li key={i} style={shellStyles.factCard}>
              <span style={shellStyles.chip(f.severity)}>{f.severity}</span>
              <strong>{f.label_pl || f.label_en}</strong>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>{f.reason_pl || f.reason_en}</div>
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ fontSize: 16, marginTop: 24 }}>{t('mos.report.procedureRequirements')}</h3>
      {proc.checklist_available === false ? (
        <div style={shellStyles.safetyStrip} data-testid="mos-report-checklist-unavailable">
          {t('mos.report.checklistUnavailable')}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 12 }}>
            {t('mos.report.verifiedItems')} {proc.verified_source_count || 0} ·{' '}
            {t('mos.report.pendingItems')} {proc.unverified_pending_source_review || 0}
          </div>
          {(proc.findings || []).length === 0 ? (
            <p style={{ color: '#94A3B8', fontSize: 14 }}>—</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0 }} data-testid="mos-report-procedure-list">
              {proc.findings.map((f, i) => (
                <li key={i} style={shellStyles.factCard}>
                  <span style={shellStyles.chip(f.severity)}>{f.severity}</span>
                  <strong>{f.label_pl || f.label_en}</strong>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                    {f.status === 'present_in_record' ? t('mos.report.statusPresent') : t('mos.report.statusMissing')}
                    {' · '}
                    {t('mos.report.sourceLabel')} {f.provenance?.source_authority || '?'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div style={{ marginTop: 32, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button style={shellStyles.primaryBtn} onClick={handleDownload} data-testid="mos-report-download">
          {t('mos.report.downloadMarkdown')}
        </button>
        <button
          style={{ ...shellStyles.secondaryBtn, ...(deleting ? shellStyles.disabledBtn : {}) }}
          disabled={deleting}
          onClick={handleDelete}
          data-testid="mos-report-delete"
        >
          {t('mos.report.deleteCase')}
        </button>
      </div>

      <div style={{ marginTop: 32, fontSize: 12, color: '#94A3B8' }} data-testid="mos-report-retention">
        <strong>{t('mos.retention.label')}:</strong> {t('mos.retention.default')} {t('mos.retention.note')}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Root component
// ----------------------------------------------------------------------------

const MosPreCheck = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [variants, setVariants] = useState(null);
  const [current, setCurrent] = useState('consent');
  const [caseInfo, setCaseInfo] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/variants`).then((r) => setVariants(r.data)).catch(() => setVariants(null));
  }, []);

  // Activation funnel: fire `landing_viewed` once per signed-in user per
  // browser session. sessionStorage is per-tab and cleared on tab close.
  useEffect(() => {
    if (!user) return;
    try {
      const key = `hussar_activation_landing_viewed_${user.id || 'anon'}`;
      if (typeof window !== 'undefined' && window.sessionStorage) {
        if (!window.sessionStorage.getItem(key)) {
          trackActivation('activation_mos_precheck_landing_viewed');
          window.sessionStorage.setItem(key, '1');
        }
      } else {
        trackActivation('activation_mos_precheck_landing_viewed');
      }
    } catch (_e) {
      /* telemetry must never break user flow */
    }
  }, [user]);

  const heroCopy = useMemo(() => ({
    eyebrow: t('mos.hero.eyebrow'),
    title: t('mos.hero.title'),
    subtitle: t('mos.hero.subtitle'),
  }), [t]);

  if (!user) {
    return (
      <div style={shellStyles.page}>
        <div style={shellStyles.container}>
          <a href="/" style={shellStyles.backLink} data-testid="mos-back-link">{t('mos.backLink')}</a>
          <div style={shellStyles.card} data-testid="mos-login-gate">
            <div style={shellStyles.eyebrow}>{heroCopy.eyebrow}</div>
            <h1 style={shellStyles.h1}>{heroCopy.title}</h1>
            <p style={shellStyles.subtitle}>{heroCopy.subtitle}</p>
            <div style={shellStyles.safetyStrip}>{t('mos.independenceStatement')}</div>
            <p style={{ fontSize: 14, color: '#CBD5F5', marginBottom: 24 }}>{t('mos.disclaimer')}</p>
            <button
              style={shellStyles.primaryBtn}
              onClick={() => navigate('/login')}
              data-testid="mos-login-cta"
            >
              {t('mos.loginRequired')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={shellStyles.page}>
      <div style={shellStyles.container}>
        <a href="/" style={shellStyles.backLink} data-testid="mos-back-link">{t('mos.backLink')}</a>
        <div style={shellStyles.eyebrow} data-testid="mos-hero-eyebrow">{heroCopy.eyebrow}</div>
        <h1 style={shellStyles.h1} data-testid="mos-hero-title">{heroCopy.title}</h1>
        <p style={shellStyles.subtitle} data-testid="mos-hero-subtitle">{heroCopy.subtitle}</p>
        <div style={shellStyles.safetyStrip} data-testid="mos-independence-strip">
          {t('mos.independenceStatement')}
        </div>
        <p style={{ fontSize: 14, color: '#CBD5F5', marginBottom: 32 }} data-testid="mos-doctrine-line">
          <em>{t('mos.doctrine')}</em>
        </p>

        <StepBar current={current} />

        {current === 'consent' && (
          <ConsentStep
            variants={variants}
            onCreated={(info) => { setCaseInfo(info); setCurrent('upload'); }}
          />
        )}
        {current === 'upload' && caseInfo && (
          <UploadStep caseInfo={caseInfo} onDone={() => setCurrent('review')} />
        )}
        {current === 'review' && caseInfo && (
          <ReviewStep caseInfo={caseInfo} onReport={(r) => { setReportData(r); setCurrent('report'); }} />
        )}
        {current === 'report' && caseInfo && (
          <ReportStep
            caseInfo={caseInfo}
            reportData={reportData}
            onDeleted={() => {
              setCaseInfo(null);
              setReportData(null);
              setCurrent('consent');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MosPreCheck;
